import React, { useState } from "react";
import { toast } from "react-toastify";
import { userService } from "../../../services/userService";
import ConfirmModal from "../../common/ConfirmModal/ConfirmModal";
import { validerImageUtilisateur } from "../../../helpers/imageUtilisateurTools";

/**
 * LS-685 — dépôt/remplacement/suppression d'une image unique d'un utilisateur.
 * Self-service par défaut (utilisateur connecté) ; `targetUuid` bascule vers les
 * routes d'administration (réservées SUPER_ADMIN côté API,
 * cf. GlobalAccess::GERER_IMAGE_UTILISATEUR), pour déposer/supprimer l'image
 * d'un autre utilisateur depuis sa fiche. `user` doit contenir la version la
 * plus à jour du user (pour recomposer `{type}Url` après suppression, la
 * réponse 204 de l'API ne renvoyant pas le user).
 *
 * LS-689 — `type` ne vaut plus que "signature" : les tampons sont devenus
 * multiples et nommés, gérés par TamponsManager.
 */
const ImageUtilisateurUploader = ({
  type,
  label,
  imageUrl,
  user,
  targetUuid,
  onChange,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;

    const erreurFichier = validerImageUtilisateur(file);
    if (erreurFichier) {
      toast.error(erreurFichier);
      return;
    }

    setIsUploading(true);
    try {
      const res = targetUuid
        ? await userService.uploadImageForUser(targetUuid, type, file)
        : await userService.uploadImage(type, file);
      onChange(res.data);
      toast.success(`${label} déposée.`);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          `Le dépôt de la ${label.toLowerCase()} a échoué.`,
      );
    } finally {
      setIsUploading(false);
    }
  }

  async function handleConfirmDelete() {
    setIsDeleting(true);
    try {
      if (targetUuid) {
        await userService.deleteImageForUser(targetUuid, type);
      } else {
        await userService.deleteImage(type);
      }
      onChange({ ...user, [`${type}Url`]: null });
      toast.success(`${label} supprimée.`);
      setIsConfirmOpen(false);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          `La suppression de la ${label.toLowerCase()} a échoué.`,
      );
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">{label}</h5>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={label}
            style={{ maxHeight: 80, display: "block", marginBottom: 12 }}
          />
        ) : (
          <p className="text-muted">Aucune {label.toLowerCase()} déposée.</p>
        )}
        <label className="btn btn-default mb-0" style={{ marginRight: 8 }}>
          <i
            className={`fa fa-${isUploading ? "spinner fa-spin" : "upload"}`}
            style={{ marginRight: 4 }}
          />
          {isUploading ? "Envoi..." : imageUrl ? "Remplacer" : "Déposer"}
          <input
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleFileChange}
            disabled={isUploading}
            hidden
          />
        </label>
        {imageUrl && (
          <button
            type="button"
            className="btn btn-default text-danger mb-0"
            onClick={() => setIsConfirmOpen(true)}
            disabled={isUploading}
          >
            <i className="fa fa-trash" style={{ marginRight: 4 }} />
            Supprimer
          </button>
        )}
      </div>

      <ConfirmModal
        isOpen={isConfirmOpen}
        title={`Supprimer la ${label.toLowerCase()}`}
        message={`Voulez-vous vraiment supprimer cette ${label.toLowerCase()} ?`}
        confirmLabel="Supprimer"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </div>
  );
};

export default ImageUtilisateurUploader;
